import { DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Card, Collapse, Modal, notification, Space, Spin, Typography } from 'antd';
import { Package, Waybill } from 'models/Waybill';
import { FC, useEffect, useRef, useState } from 'react';
import { downloadWaybill, generateWaybillFileName } from 'utils/waybillDownload';

interface WaybillViewerProps {
  packagesWithWaybill: Package[];
  sparingRequestName: string;
  open: boolean;
  onClose: () => void;
}

export const WaybillViewer: FC<WaybillViewerProps> = ({ packagesWithWaybill, sparingRequestName, open, onClose }) => {
  const [isDownloading, setIsDownloading] = useState<Record<string, boolean>>({});
  const [expandedWaybills, setExpandedWaybills] = useState<Record<string, boolean>>({});
  const [iframeLoading, setIframeLoading] = useState<Record<string, boolean>>({});
  const iframeRefs = useRef<Record<string, HTMLIFrameElement | null>>({});

  // Reset states when modal closes
  useEffect(() => {
    if (!open) {
      setExpandedWaybills({});
      setIframeLoading({});
      iframeRefs.current = {};
    }
  }, [open]);

  const handleIframeLoad = (trackingNumber: string) => {
    setIframeLoading((prev) => ({
      ...prev,
      [trackingNumber]: false
    }));
  };

  const handleDownload = async (waybill: Waybill, packageIndex: number, waybillIndex: number, pkg: Package, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      setIsDownloading((prev) => ({ ...prev, [waybill.trackingNumber]: true }));

      const fileName = generateWaybillFileName(sparingRequestName, packageIndex, waybillIndex, pkg.waybill?.length || 0, waybill.trackingNumber);

      await downloadWaybill(waybill, fileName);

      notification.success({
        message: 'Download Started',
        description: `Waybill ${waybill.trackingNumber} download has started`,
        duration: 3
      });
    } catch (error) {
      notification.error({
        message: 'Download Failed',
        description: `Failed to download the waybill. Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        duration: 5
      });
    } finally {
      setIsDownloading((prev) => ({ ...prev, [waybill.trackingNumber]: false }));
    }
  };

  const handleDownloadAllForPackage = async (pkg: Package, packageIndex: number, e: React.MouseEvent) => {
    e.stopPropagation();

    if (!pkg.waybill?.length) {
      notification.warning({
        message: 'No Waybills',
        description: 'No waybills found to download for this package',
        duration: 3
      });

      return;
    }

    try {
      notification.info({
        message: 'Downloading Waybills',
        description: `Starting download for ${pkg.waybill.length} waybill${pkg.waybill.length > 1 ? 's' : ''} from Package ${packageIndex + 1}`,
        duration: 3
      });

      for (let i = 0; i < pkg.waybill.length; i++) {
        const waybill = pkg.waybill[i];

        setIsDownloading((prev) => ({ ...prev, [waybill.trackingNumber]: true }));

        const fileName = generateWaybillFileName(sparingRequestName, packageIndex, i, pkg.waybill.length, waybill.trackingNumber);

        await downloadWaybill(waybill, fileName);

        // Small delay between downloads to prevent browser blocking
        await new Promise((resolve) => setTimeout(resolve, 500));
        setIsDownloading((prev) => ({ ...prev, [waybill.trackingNumber]: false }));
      }

      notification.success({
        message: 'Downloads Started',
        description: `Started downloading ${pkg.waybill.length} waybill${pkg.waybill.length > 1 ? 's' : ''} from Package ${packageIndex + 1}`,
        duration: 3
      });
    } catch (error) {
      notification.error({
        message: 'Download Failed',
        description: `Failed to download some waybills. Error: ${error instanceof Error ? error.message : 'Unknown error occurred'}`,
        duration: 5
      });
    }
  };

  const toggleWaybillExpand = (trackingNumber: string) => {
    setExpandedWaybills((prev) => {
      const newState = {
        ...prev,
        [trackingNumber]: !prev[trackingNumber]
      };

      if (newState[trackingNumber]) {
        setIframeLoading((prevLoading) => ({
          ...prevLoading,
          [trackingNumber]: true
        }));
      }

      return newState;
    });
  };

  // Always show modal, even if no packages exist

  return (
    <Modal
      title={
        <Typography.Title level={4} style={{ margin: 0 }}>
          {sparingRequestName} - Waybill Viewer ({packagesWithWaybill.length} package{packagesWithWaybill.length !== 1 ? 's' : ''})
        </Typography.Title>
      }
      open={open}
      onCancel={onClose}
      width="90%"
      style={{ top: 20 }}
      footer={null}>
      {packagesWithWaybill.length > 0 ? (
        <Collapse defaultActiveKey={[0]} style={{ background: '#fff' }}>
          {packagesWithWaybill.map((pkg, packageIndex) => (
            <Collapse.Panel
              key={packageIndex}
              style={{ marginBottom: '8px' }}
              header={
                <Space>
                  <Typography.Text strong>Package {packageIndex + 1}:</Typography.Text>
                  <Typography.Text>{pkg.numberOfPackages} items</Typography.Text>
                  <Typography.Text>Size: {pkg.packageSize}</Typography.Text>
                  <Typography.Text>Weight: {pkg.packageWeight} lbs</Typography.Text>
                  {pkg.waybill?.[0]?.masterTrackingNumber && <Typography.Text>Master Tracking: {pkg.waybill[0].masterTrackingNumber}</Typography.Text>}
                  <Button icon={<DownloadOutlined />} size="small" onClick={(e) => handleDownloadAllForPackage(pkg, packageIndex, e)} title="Download All Waybills for this Package">
                    Download All
                  </Button>
                </Space>
              }>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pkg.waybill?.map((waybill, waybillIndex) => (
                  <Card
                    key={waybill.trackingNumber}
                    size="small"
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <Typography.Text strong style={{ marginRight: 'auto' }}>
                          Tracking: {waybill.trackingNumber}
                        </Typography.Text>
                        <Button
                          icon={<EyeOutlined />}
                          size="small"
                          type={expandedWaybills[waybill.trackingNumber] ? 'primary' : 'default'}
                          onClick={() => toggleWaybillExpand(waybill.trackingNumber)}
                          title={expandedWaybills[waybill.trackingNumber] ? 'Hide Waybill' : 'View Waybill'}
                        />
                        <Button
                          icon={<DownloadOutlined />}
                          size="small"
                          loading={isDownloading[waybill.trackingNumber]}
                          onClick={(e) => handleDownload(waybill, packageIndex, waybillIndex, pkg, e)}
                          title="Download PDF"
                        />
                      </div>
                    }
                    style={{ width: '100%', marginBottom: 0 }}
                    bodyStyle={{ padding: expandedWaybills[waybill.trackingNumber] ? '12px' : 0 }}>
                    {expandedWaybills[waybill.trackingNumber] && (
                      <div style={{ height: '60vh', position: 'relative' }}>
                        {iframeLoading[waybill.trackingNumber] && (
                          <div
                            style={{
                              position: 'absolute',
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              background: 'rgba(255, 255, 255, 0.8)'
                            }}>
                            <Spin size="large" />
                          </div>
                        )}
                        <div style={{ height: '100%', width: '100%' }}>
                          <iframe
                            ref={(el) => (iframeRefs.current[waybill.trackingNumber] = el)}
                            src={`https://docs.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(waybill.url)}&pid=explorer&efh=false&a=v`}
                            width="100%"
                            height="100%"
                            style={{ border: 'none' }}
                            title={`Waybill PDF Viewer - ${waybill.trackingNumber}`}
                            onLoad={() => handleIframeLoad(waybill.trackingNumber)}
                            sandbox="allow-scripts allow-same-origin allow-popups"
                          />
                        </div>
                      </div>
                    )}
                  </Card>
                ))}
              </div>
            </Collapse.Panel>
          ))}
        </Collapse>
      ) : (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <Typography.Text type="secondary">No waybill packages found for this request.</Typography.Text>
          <br />
          <Typography.Text type="secondary" style={{ fontSize: '14px' }}>
            Generate waybills first to view them here.
          </Typography.Text>
        </div>
      )}
    </Modal>
  );
};
